import { CMMS_RECORD_TYPES } from 'features/knowledgeDocumentViewer/workOrders/cmmsRecordTypes'
import { lotoReportSchema } from 'features/lotoReports/lotoReportSchemas'
import { GECKO_LOCALES } from 'types/measurement/geckoLocale'
import { z } from 'zod'

import {
  InspectionResultSchema,
  InspectionViewContextSchema,
} from './inspectionResultSchema'

export type SearchEngine = 'unified' | 'docChat'

export const SearchSourceSchema = z.enum(['elasticsearch', 'pinecone'])

export const HighlightSchema = z.object({
  page_num: z.number(),
  page_id: z.string().nullable().optional(),
  fragments: z.array(z.string()),
})

export const PageClassificationSchema = z.object({
  page_num: z.number(),
  category: z.string().nullable().optional(),
  sheet_number: z.string().nullable().optional(),
  drawing_number: z.string().nullable().optional(),
  revision_code: z.string().nullable().optional(),
  revision_date: z.string().nullable().optional(),
  rich_text_summary: z.string().nullable().optional(),
})

export const DrawingRevisionOptionSchema = z.object({
  document_id: z.string(),
  highlights: z.array(HighlightSchema).nullable().optional(),
  page_classifications: z.array(PageClassificationSchema).nullable().optional(),
  drawing_revision_groups: z.array(z.unknown()).nullable().optional(),
})

export const DrawingRevisionGroupSchema = z.object({
  page_num: z.number(),
  drawing_number: z.string(),
  sheet_number: z.string().nullable().optional(),
  options: z.array(DrawingRevisionOptionSchema),
})

export const SearchResultDocumentSchema = z.object({
  document_id: z.string(),
  score: z.number(),
  sources: z.array(SearchSourceSchema),
  elasticsearch_score: z.number().nullable().optional(),
  pinecone_score: z.number().nullable().optional(),
  highlights: z.array(HighlightSchema).nullable().optional(),
  justification: z.string().nullable().optional(),
  page_classifications: z.array(PageClassificationSchema).nullable().optional(),
  drawing_revision_groups: z
    .array(DrawingRevisionGroupSchema)
    .nullable()
    .optional(),
})

export const UnifiedSearchRequestSchema = z.strictObject({
  query: z.string(),
  site_id: z.string(),
  limit: z.number().int().min(1).max(200).optional(),
  preview_mode: z.boolean().optional(),
})

export const DocChatImageAttachmentSchema = z.strictObject({
  mime_type: z.enum(['image/png', 'image/jpeg', 'image/webp']),
  data_base64: z.string(),
  filename: z.string().optional(),
})

export const MAX_CONTEXT_DOCUMENT_IDS = 50

export const DocChatContextDocumentSchema = z.object({
  /** Bare DOC-{hash}, never pipeline-config scoped. */
  document_id: z.string().min(1),
  /** 1-based page the user is on; set for the active document. */
  page_num: z.number().int().positive().optional(),
  title: z.string().optional(),
  is_active: z.boolean().optional(),
})

export type DocChatContextDocument = z.infer<
  typeof DocChatContextDocumentSchema
>

export const DocChatRequestSchema = UnifiedSearchRequestSchema.extend({
  conversation_id: z.string().optional(),
  /** Backend stores the turn under this id, so history matches the live turn. */
  turn_id: z
    .string()
    .regex(/^[A-Za-z0-9_-]{1,128}$/)
    .optional(),
  model: z.string().optional(),
  thinking_level: z.string().optional(),
  enable_time_series: z.boolean().optional(),
  enable_cmms: z.boolean().optional(),
  enable_multi_call: z.boolean().optional(),
  timezone: z.string().optional(),
  tag_selections: z
    .array(z.object({ identifier: z.string(), entry_id: z.string() }))
    .optional(),
  time_series_protocol: z.number().int().min(1).max(2).optional(),
  viewer: z.lazy(() => TimeSeriesChartStateSchema).optional(),
  inspection_context: InspectionViewContextSchema.optional(),
  enable_image_ingestion: z.boolean().optional(),
  attachments: z.array(DocChatImageAttachmentSchema).optional(),
  locale: z.enum(GECKO_LOCALES).optional(),
  artifact_id: z.string().optional(),
  context_document_ids: z
    .array(z.string())
    .max(MAX_CONTEXT_DOCUMENT_IDS)
    .optional(),
  context_documents: z
    .array(DocChatContextDocumentSchema)
    .max(MAX_CONTEXT_DOCUMENT_IDS)
    .optional(),
})

export const DocChatImageReadingValueSchema = z.strictObject({
  label: z.string(),
  value: z.string(),
  units: z.string().nullable().optional(),
})

export const DocChatImageNameplateSchema = z.strictObject({
  manufacturer: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  serial: z.string().nullable().optional(),
  ratings: z.array(z.string()).optional(),
})

export const DocChatImageReadingSchema = z.strictObject({
  image_kind: z.enum([
    'nameplate',
    'gauge',
    'pid_snippet',
    'screenshot',
    'photo',
    'other',
  ]),
  summary: z.string(),
  tags: z.array(z.string()).optional(),
  equipment_ids: z.array(z.string()).optional(),
  drawing_numbers: z.array(z.string()).optional(),
  document_titles: z.array(z.string()).optional(),
  readings: z.array(DocChatImageReadingValueSchema).optional(),
  nameplate: DocChatImageNameplateSchema.nullable().optional(),
  unreadable_notes: z.array(z.string()).optional(),
})

export const UnifiedDocumentSearchResultSchema = z.object({
  query: z.string(),
  site_id: z.string(),
  weights: z.object({
    lexical_weight: z.number(),
    semantic_weight: z.number(),
  }),
  results: z.array(SearchResultDocumentSchema),
})

export const CitationFailureReasonSchema = z.enum(['quote_not_in_page_text'])

export const PageTextCitationSchema = z.object({
  type: z.literal('page_text'),
  document_id: z.string(),
  page_id: z.string(),
  page_num: z.number(),
  quote: z.string(),
  failure_reason: CitationFailureReasonSchema.nullable().optional(),
  match_method: z.enum(['exact', 'whitespace']).nullable().optional(),
})

export const DatapointValueCitationSchema = z.strictObject({
  type: z.literal('datapoint_value'),
  entry_id: z.string(),
  pi_tag: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  units: z.string().nullable().optional(),
  value: z.number().nullable().optional(),
  timestamp: z.string().nullable().optional(),
  aggregation_op: z.string().nullable().optional(),
})

export const CmmsSearchResultSchema = z.object({
  entry_id: z.string(),
  record_type: z.enum(CMMS_RECORD_TYPES).default('cmms-work-order'),
  number: z.string(),
  title: z.string(),
  status: z.string().nullable().optional(),
  status_label: z.string().nullable().optional(),
  asset_number: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  changed_at: z.string().nullable().optional(),
  synced_at: z.string().nullable().optional(),
  version: z.string(),
  excerpt: z.string(),
  score: z.number(),
  sources: z.array(z.enum(['lexical', 'semantic', 'filtered'])),
})

export const DocChatCmmsDataSchema = z.object({
  records: z.array(CmmsSearchResultSchema),
  notes: z.array(z.string()),
  truncated: z.boolean(),
})
export type CmmsSearchResult = z.infer<typeof CmmsSearchResultSchema>
export type DocChatCmmsData = z.infer<typeof DocChatCmmsDataSchema>

export const CmmsRecordCitationSchema = z.object({
  document_revision: z.string().nullable().optional(),
  node_ids: z.array(z.string()).optional(),
  type: z.literal('cmms_record'),
  entry_id: z.string(),
  record_type: z.enum(CMMS_RECORD_TYPES).default('cmms-work-order'),
  number: z.string(),
  title: z.string(),
  status: z.string().nullable().optional(),
  quote: z.string(),
  version: z.string(),
  failure_reason: z.literal('quote_not_in_record_text').nullable().optional(),
})

/**
 * The inspection analysis that a statement's values come from: one component's
 * analysis, not one value. Not a strict object, so that fields the backend adds
 * later do not reject the whole response.
 */
export const InspectionAnalysisCitationSchema = z.object({
  type: z.literal('inspection_analysis'),
  /** Any inspection tool: a tool added later must not reject the response. */
  tool: z.string(),
  unit_id: z.string(),
  asset: z.string().nullable().optional(),
  component_id: z.string().nullable().optional(),
  component: z.string().nullable().optional(),
  inspection_id: z.string().nullable().optional(),
  inspection_date: z.string().nullable().optional(),
  data_state: z.string().nullable().optional(),
  tmin_method: z.string().nullable().optional(),
})

export const CitationSchema = z.discriminatedUnion('type', [
  PageTextCitationSchema,
  DatapointValueCitationSchema,
  CmmsRecordCitationSchema,
  InspectionAnalysisCitationSchema,
])

export const ResponseSegmentSchema = z.object({
  text: z.string(),
  citations: z.array(CitationSchema),
  /**
   * `details` shows the segment as a collapsed block; `suggestions` shows its
   * questions as buttons. Other values show it as a normal segment.
   */
  kind: z
    .enum(['details', 'suggestions'])
    .nullable()
    .optional()
    .catch(undefined),
  /** Label of the collapsed block for a `details` segment. */
  title: z.string().nullable().optional(),
  /** Questions of a `suggestions` segment; `text` is the fallback without them. */
  suggestions: z.array(z.string()).nullable().optional().catch(undefined),
})

export const TimeSeriesDatapointMatchSchema = z.strictObject({
  entry_id: z.string(),
  pi_tag: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  units: z.string().nullable().optional(),
  latest_value: z.number().nullable().optional(),
  latest_timestamp: z.string().nullable().optional(),
  point_type: z.string().nullable().optional(),
  measurement: z.string().nullable().optional(),
  span: z.number().nullable().optional(),
  zero: z.number().nullable().optional(),
  digital_set: z.string().nullable().optional(),
  notes: z.array(z.string()).nullable().optional(),
})

export const TimeSeriesIdentifierResolutionSchema = z.strictObject({
  identifier: z.string(),
  status: z.enum(['resolved', 'ambiguous', 'missing']),
  matches: z.array(TimeSeriesDatapointMatchSchema),
})

export const TimeSeriesSeriesStatsSchema = z.strictObject({
  count: z.number(),
  min: z.number().nullable().optional(),
  max: z.number().nullable().optional(),
  mean: z.number().nullable().optional(),
  first: z.number().nullable().optional(),
  last: z.number().nullable().optional(),
})

export const TimeSeriesSeriesSummarySchema = z.strictObject({
  entry_id: z.string(),
  pi_tag: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  units: z.string().nullable().optional(),
  latest_value: z.number().nullable().optional(),
  latest_timestamp: z.string().nullable().optional(),
  stats: TimeSeriesSeriesStatsSchema.nullable().optional(),
  computed: z.boolean().nullable().optional(),
  coverage: z.number().nullable().optional(),
})

export const TimeSeriesRequestEchoSchema = z.strictObject({
  start_datetime: z.string().nullable().optional(),
  end_datetime: z.string().nullable().optional(),
  interval: z.number().nullable().optional(),
  interval_unit: z.string().nullable().optional(),
  aggregation_op: z.string(),
  basis: z.string().nullable().optional(),
  gate_description: z.string().nullable().optional(),
})

export const TimeSeriesComputedSeriesSchema = z.strictObject({
  entry_id: z.string(),
  label: z.string(),
  units: z.string().nullable().optional(),
  base_entry_id: z.string().nullable().optional(),
  points: z.array(z.tuple([z.string(), z.number().nullable()])),
})

export const TimeSeriesYRangeSchema = z.strictObject({
  min: z.number().nullable().optional(),
  max: z.number().nullable().optional(),
})

export const TimeSeriesChartBandSchema = z.strictObject({
  start: z.string(),
  end: z.string(),
  label: z.string().nullable().optional(),
})

/** What a chart shows, as the client reports it or acknowledges a command. */
export const TimeSeriesChartStateSchema = z.strictObject({
  chart_id: z.string().nullable().optional(),
  revision: z.number().nullable().optional(),
  pens: z.array(z.string()).optional(),
  computed_pens: z.array(z.string()).optional(),
  hidden_pens: z.array(z.string()).optional(),
  start_datetime: z.string().nullable().optional(),
  end_datetime: z.string().nullable().optional(),
  interval: z.number().nullable().optional(),
  interval_unit: z.string().nullable().optional(),
  aggregation_op: z.string().nullable().optional(),
  y_ranges: z.record(z.string(), TimeSeriesYRangeSchema).nullable().optional(),
  band: TimeSeriesChartBandSchema.nullable().optional(),
})

export const TimeSeriesChartCommandOpSchema = z.enum([
  'focus',
  'add_pens',
  'remove_pens',
  'replace_pens',
])

/** A chart change the agent asked for; the client applies it and acknowledges. */
export const TimeSeriesChartCommandSchema = z.strictObject({
  command_id: z.string(),
  op: TimeSeriesChartCommandOpSchema,
  pens: z.array(z.string()).optional(),
  computed_series: z
    .array(TimeSeriesComputedSeriesSchema)
    .nullable()
    .optional(),
  hide: z.array(z.string()).optional(),
  start_datetime: z.string().nullable().optional(),
  end_datetime: z.string().nullable().optional(),
  interval: z.number().nullable().optional(),
  interval_unit: z.string().nullable().optional(),
  aggregation_op: z.string().nullable().optional(),
  y_ranges: z.record(z.string(), TimeSeriesYRangeSchema).nullable().optional(),
  band: TimeSeriesChartBandSchema.nullable().optional(),
  label: z.string().nullable().optional(),
  note: z.string().optional(),
  acknowledged: z.boolean().nullable().optional(),
  applied_state: TimeSeriesChartStateSchema.nullable().optional(),
})

export const TimeSeriesDatasetSummarySchema = z.strictObject({
  dataset_id: z.string(),
  result_id: z.string(),
  spec: z.record(z.string(), z.unknown()).optional(),
  columns: z.array(z.string()).optional(),
  rows: z.number().optional(),
  retrieved_at: z.string().nullable().optional(),
})

export const TimeSeriesTrendRefSchema = z.strictObject({
  entry_ids: z.array(z.string()).min(1),
  label: z.string(),
  start_datetime: z.string().nullable().optional(),
  end_datetime: z.string().nullable().optional(),
  computed_series: z
    .array(TimeSeriesComputedSeriesSchema)
    .nullable()
    .optional(),
  context_entry_ids: z.array(z.string()).nullable().optional(),
  replace_pens: z.boolean().nullable().optional(),
  interval: z.number().nullable().optional(),
  interval_unit: z.string().nullable().optional(),
  aggregation_op: z.string().nullable().optional(),
  y_ranges: z.record(z.string(), TimeSeriesYRangeSchema).nullable().optional(),
  command_id: z.string().nullable().optional(),
})

export const DocChatTimeSeriesDataSchema = z.strictObject({
  resolutions: z.array(TimeSeriesIdentifierResolutionSchema),
  series: z.array(TimeSeriesSeriesSummarySchema),
  request: TimeSeriesRequestEchoSchema.nullable().optional(),
  trend: TimeSeriesTrendRefSchema.nullable().optional(),
  notes: z.array(z.string()),
  chart_commands: z.array(TimeSeriesChartCommandSchema).nullable().optional(),
  datasets: z.array(TimeSeriesDatasetSummarySchema).nullable().optional(),
  chart_state: TimeSeriesChartStateSchema.nullable().optional(),
  cited_series: z.array(TimeSeriesComputedSeriesSchema).nullable().optional(),
})

export const DocChatQueryPlanSchema = z.looseObject({
  elasticsearch_query: z.string(),
  pinecone_query: z.string(),
  elasticsearch_weight: z.number().min(0).max(1),
  pinecone_weight: z.number().min(0).max(1),
  final_response: z.string().min(1),
})

export const DocChatMetadataSchema = z.object({
  query_plan: DocChatQueryPlanSchema,
  is_fallback: z.boolean(),
  /** `false` when the turn ran no document search; older backends omit it. */
  searched_documents: z.boolean().nullable().optional(),
})

/**
 * null = unknown / not measured; 0 = a measured zero. Card fields are `.nullish()`
 * (not `.nullable()`): the backend persists the Rolodex conversation snapshot with
 * `exclude_none`, so on reload a null field comes back as an ABSENT key, and the
 * schema must accept both the live `null` and the restored missing key.
 */
const NullableCountSchema = z.number().int().nullable()
const NullableCountsSchema = z.record(z.string(), NullableCountSchema)
const NullishCountSchema = z.number().int().nullish()

export const RevisionDiffResultSchema = z.object({
  kind: z.literal('diff'),
  from_document_id: z.string(),
  from_revision: z.string().nullish(),
  from_page_num: NullishCountSchema,
  to_document_id: z.string(),
  to_revision: z.string().nullish(),
  to_page_num: NullishCountSchema,
  counts: z.object({
    by_status: NullableCountsSchema,
    by_impact: NullableCountsSchema,
  }),
  verdict: z.string().nullish(),
  headline_events: z.array(
    z.object({
      headline: z.string(),
      impact: z.string().nullish(),
    })
  ),
})

export const RevisionMissingResultSchema = z.object({
  kind: z.literal('missing'),
  drawing_number: z.string().nullish(),
  sheet_number: z.string().nullish(),
  audit_state: z.string().nullish(),
  /** Derived backend-side from every completeness input below. Always sent. */
  audit_complete: z.boolean(),
  /** Backend-derived: complete, all declared available, nothing missing or needing review. */
  nothing_missing_confirmed: z.boolean(),
  findings_unavailable: z.boolean().nullish(),
  inventory_in_scope: z.boolean().nullish(),
  table_scan_complete: z.boolean().nullish(),
  inventory_search_complete: z.boolean().nullish(),
  unreadable_member_count: NullishCountSchema,
  missing_count: NullishCountSchema,
  needs_review_count: NullishCountSchema,
  available_count: NullishCountSchema,
  missing_revisions: z.array(
    z.object({
      label: z.string().nullish(),
      date: z.string().nullish(),
      description: z.string().nullish(),
    })
  ),
  jump_ref: z
    .object({
      document_id: z.string(),
      page_num: NullishCountSchema,
    })
    .nullish(),
})

export const RevisionResultSchema = z.discriminatedUnion('kind', [
  RevisionDiffResultSchema,
  RevisionMissingResultSchema,
])

export type RevisionDiffResult = z.infer<typeof RevisionDiffResultSchema>
export type RevisionMissingResult = z.infer<typeof RevisionMissingResultSchema>
export type RevisionResult = z.infer<typeof RevisionResultSchema>

export const DocChatLotoReportSchema = lotoReportSchema
  .optional()
  .catch(undefined)

export const DocChatResponseEventSchema = z
  .object({
    type: z.literal('response'),
    cmms: DocChatCmmsDataSchema.nullable().optional(),
    query: z.string(),
    site_id: z.string(),
    conversation_id: z.string(),
    final_response: z.string().optional(),
    response: z.string().optional(),
    response_segments: z.array(ResponseSegmentSchema).optional(),
    metadata: DocChatMetadataSchema,
    results: z.array(SearchResultDocumentSchema),
    time_series: DocChatTimeSeriesDataSchema.optional(),
    revision_results: z.array(RevisionResultSchema).optional(),
    inspection_results: z.array(InspectionResultSchema).optional(),
    image_reading: DocChatImageReadingSchema.nullable().optional(),
    loto_report: DocChatLotoReportSchema,
  })
  .refine(
    (data) => {
      const hasFinalResponse =
        data.final_response !== undefined &&
        data.final_response.trim().length > 0
      const hasResponse =
        data.response !== undefined && data.response.trim().length > 0
      const hasSegmentText =
        data.response_segments !== undefined &&
        data.response_segments.some((segment) => segment.text.trim().length > 0)
      return hasFinalResponse || hasResponse || hasSegmentText
    },
    {
      error:
        'Response event requires a non-empty final_response, response, or response_segments text',
    }
  )

export const DocChatSseThoughtEventSchema = z.object({
  type: z.literal('thought'),
  query: z.string(),
  site_id: z.string(),
  conversation_id: z.string(),
  message: z.string().min(1),
})

export const DocChatSseErrorEventSchema = z.object({
  type: z.literal('error'),
  query: z.string(),
  site_id: z.string(),
  conversation_id: z.string().nullable().optional(),
  message: z.string().min(1),
})

export const DocChatResponsePartialEventSchema = z.object({
  type: z.literal('response_partial'),
  cmms: DocChatCmmsDataSchema.nullable().optional(),
  query: z.string(),
  site_id: z.string(),
  conversation_id: z.string(),
  response: z.string(),
  response_segments: z.array(ResponseSegmentSchema),
  results: z.array(SearchResultDocumentSchema).default([]),
})

export const ArtifactKindSchema = z.enum(['document'])

export const ArtifactDownloadEventSchema = z.object({
  type: z.literal('downloaded'),
  at: z.string(),
  user_id: z.string().nullable(),
  revision: z.number().int(),
  file_path: z.string(),
})

export const DocumentArtifactSectionSchema = z.strictObject({
  section_id: z.string(),
  heading: z.string(),
  body: z.string(),
})

export const ArtifactProvenanceSourceSchema = z.enum([
  'document',
  'conversation',
  'inferred',
  'default',
])

export const DocumentArtifactSectionProvenanceSchema = z.strictObject({
  source: ArtifactProvenanceSourceSchema,
  citations: z.array(PageTextCitationSchema),
  needs_review: z.boolean(),
  note: z.string().nullable(),
})

export const ArtifactOpenItemSchema = z.strictObject({
  section_id: z.string().nullable(),
  reason: z.string(),
})

export const DocumentArtifactContentSchema = z.strictObject({
  sections: z.array(DocumentArtifactSectionSchema),
  provenance: z.record(z.string(), DocumentArtifactSectionProvenanceSchema),
  open_items: z.array(ArtifactOpenItemSchema),
})

/** Unknown top-level keys are stripped so a backend addition never breaks the viewer; each kind's `content` stays strict. */
const ArtifactBaseSchema = z.object({
  artifact_id: z.string(),
  site_id: z.string(),
  site_name: z.string().nullable().optional(),
  conversation_id: z.string(),
  kind_version: z.number().int(),
  title: z.string(),
  producer: z.object({ agent: z.string() }),
  created_by_user_id: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  revision: z.number().int(),
  events: z.array(ArtifactDownloadEventSchema),
})

export const DocumentArtifactSchema = ArtifactBaseSchema.extend({
  kind: z.literal('document'),
  content: DocumentArtifactContentSchema,
})

export const ArtifactSchema = z.discriminatedUnion('kind', [
  DocumentArtifactSchema,
])

export const DocChatSseArtifactEventSchema = z.object({
  type: z.literal('artifact'),
  query: z.string(),
  site_id: z.string(),
  conversation_id: z.string(),
  artifact: ArtifactSchema,
  changed_paths: z.array(z.string()),
})

export const DocChatSseStartedEventSchema = z.object({
  type: z.literal('started'),
  conversation_id: z.string().min(1),
  turn_id: z.string().min(1),
  status: z.literal('running'),
})

export const DocChatSseChartCommandEventSchema = z.object({
  type: z.literal('chart_command'),
  query: z.string(),
  site_id: z.string(),
  conversation_id: z.string(),
  command: TimeSeriesChartCommandSchema,
})

export const DocChatSseEventSchema = z.union([
  DocChatSseThoughtEventSchema,
  DocChatResponsePartialEventSchema,
  DocChatResponseEventSchema,
  DocChatSseErrorEventSchema,
  DocChatSseArtifactEventSchema,
  DocChatSseStartedEventSchema,
  DocChatSseChartCommandEventSchema,
])

/** The client's answer to a chart_command event. */
export interface DocChatChartAckRequest {
  conversation_id: string
  command_id: string
  applied: boolean
  state?: TimeSeriesChartState
  error?: string
}

export const DocChatChartAckResponseSchema = z.object({ accepted: z.boolean() })

export type Highlight = z.infer<typeof HighlightSchema>
export type PageClassification = z.infer<typeof PageClassificationSchema>
export type DrawingRevisionOption = z.infer<typeof DrawingRevisionOptionSchema>
export type DrawingRevisionGroup = z.infer<typeof DrawingRevisionGroupSchema>
export type CitationFailureReason = z.infer<typeof CitationFailureReasonSchema>
export type PageTextCitation = z.infer<typeof PageTextCitationSchema>
export type DatapointValueCitation = z.infer<
  typeof DatapointValueCitationSchema
>
export type Citation = z.infer<typeof CitationSchema>
export type CmmsRecordCitation = z.infer<typeof CmmsRecordCitationSchema>
export type InspectionAnalysisCitation = z.infer<
  typeof InspectionAnalysisCitationSchema
>
export type TimeSeriesDatapointMatch = z.infer<
  typeof TimeSeriesDatapointMatchSchema
>
export type TimeSeriesIdentifierResolution = z.infer<
  typeof TimeSeriesIdentifierResolutionSchema
>
export type TimeSeriesSeriesSummary = z.infer<
  typeof TimeSeriesSeriesSummarySchema
>
export type TimeSeriesTrendRef = z.infer<typeof TimeSeriesTrendRefSchema>
export type TimeSeriesComputedSeries = z.infer<
  typeof TimeSeriesComputedSeriesSchema
>
export type TimeSeriesYRange = z.infer<typeof TimeSeriesYRangeSchema>
export type TimeSeriesChartBand = z.infer<typeof TimeSeriesChartBandSchema>
export type TimeSeriesChartState = z.infer<typeof TimeSeriesChartStateSchema>
export type TimeSeriesChartCommand = z.infer<
  typeof TimeSeriesChartCommandSchema
>
export type DocChatSseChartCommandEvent = z.infer<
  typeof DocChatSseChartCommandEventSchema
>
export type DocChatTimeSeriesData = z.infer<typeof DocChatTimeSeriesDataSchema>
export type ResponseSegment = z.infer<typeof ResponseSegmentSchema>
export type SearchResultDoc = z.infer<typeof SearchResultDocumentSchema>
export type UnifiedDocumentSearchResult = z.infer<
  typeof UnifiedDocumentSearchResultSchema
>
export type DocChatMetadata = z.infer<typeof DocChatMetadataSchema>
export type DocChatSseThoughtEvent = z.infer<
  typeof DocChatSseThoughtEventSchema
>
export type DocChatSseErrorEvent = z.infer<typeof DocChatSseErrorEventSchema>
export type DocChatSseResponsePartialEvent = z.infer<
  typeof DocChatResponsePartialEventSchema
>
export type DocChatSseArtifactEvent = z.infer<
  typeof DocChatSseArtifactEventSchema
>
export type DocChatSseStartedEvent = z.infer<
  typeof DocChatSseStartedEventSchema
>
export type DocChatSseEvent = z.infer<typeof DocChatSseEventSchema>
export type ArtifactKind = z.infer<typeof ArtifactKindSchema>
export type Artifact = z.infer<typeof ArtifactSchema>
export type DocumentArtifact = z.infer<typeof DocumentArtifactSchema>
export type DocumentArtifactSection = z.infer<
  typeof DocumentArtifactSectionSchema
>
export type ArtifactOpenItem = z.infer<typeof ArtifactOpenItemSchema>
export type ArtifactDownloadEvent = z.infer<typeof ArtifactDownloadEventSchema>
export type DocChatSearchResult = z.infer<typeof DocChatResponseEventSchema>
export type DocChatImageAttachment = z.infer<
  typeof DocChatImageAttachmentSchema
>
export type DocChatImageReading = z.infer<typeof DocChatImageReadingSchema>

export type DocumentSearchResult =
  | UnifiedDocumentSearchResult
  | DocChatSearchResult

export function isDocChatKeepaliveSseEvent(
  event: DocChatSseEvent
): event is Extract<
  DocChatSseEvent,
  {
    type:
      | 'thought'
      | 'response_partial'
      | 'artifact'
      | 'started'
      | 'chart_command'
  }
> {
  switch (event.type) {
    case 'thought':
    case 'response_partial':
    case 'artifact':
    case 'started':
    case 'chart_command':
      return true
    case 'response':
    case 'error':
      return false
  }
}

export function parseDocChatSseData(rawData: string): DocChatSseEvent {
  const parsed: unknown = JSON.parse(rawData)
  return DocChatSseEventSchema.parse(parsed)
}

/** Parse one SSE payload; unknown/empty events are ignored so streaming keepalives cannot fail the search. */
export function readDocChatSseEvent(
  rawData: string
): DocChatSseEvent | undefined {
  if (rawData.trim().length === 0) return undefined
  try {
    return parseDocChatSseData(rawData)
  } catch {
    return undefined
  }
}

export function isDocChatSearchResult(
  result: DocumentSearchResult | undefined
): result is DocChatSearchResult {
  return !!result && 'type' in result && result.type === 'response'
}

export function getDocumentSearchWeights(
  result: DocumentSearchResult | undefined
):
  | {
      lexical_weight: number
      semantic_weight: number
    }
  | undefined {
  if (!result) return undefined
  if (isDocChatSearchResult(result)) {
    return {
      lexical_weight: result.metadata.query_plan.elasticsearch_weight,
      semantic_weight: result.metadata.query_plan.pinecone_weight,
    }
  }
  return result.weights
}
